// Çerez onayı (KVKK Çerez Uygulamaları Rehberi 2022, GDPR/ePrivacy): saf durum mantığı ve onay çerezi biçimi.
// Kategoriler: "Zorunlu" (her zaman açık, onay gerekmez: bu onay çerezinin kendisi) ve "Analitik" (Google Analytics 4,
// yalnızca açık onayla). Pazarlama kategorisi yok (sitede reklam/yeniden pazarlama yok). Ayrıntı: docs/analytics.md

/** Birinci taraf onay çerezi (zorunlu çerez: tercihi hatırlar; içinde kişisel veri yok) */
export const CONSENT_COOKIE = "fx_cerez";

/** Kayıtlı tercih: politika sürümü, analitik izni, karar zamanı (Unix saniye) */
export type ConsentRecord = { version: string; analytics: boolean; decidedAt: number };

/** "unknown" = sorulmadı / süresi doldu / politika sürümü değişti → bant gösterilir */
export type ConsentStatus = "granted" | "denied" | "unknown";

/** Politika sürümü: panelden; yalnızca harf, rakam, nokta, tire, alt çizgi (çerez değerinde güvenli) */
export const POLICY_VERSION_PATTERN = /^[A-Za-z0-9._-]{1,20}$/;
export const DEFAULT_POLICY_VERSION = "1";
export const CONSENT_MONTH_OPTIONS = [6, 12] as const;
export const DEFAULT_CONSENT_MONTHS = 6;

export function normalizePolicyVersion(value: string | undefined | null): string {
  const v = (value ?? "").trim();
  return POLICY_VERSION_PATTERN.test(v) ? v : DEFAULT_POLICY_VERSION;
}

export function normalizeConsentMonths(value: string | number | undefined | null): number {
  const n = Number(value);
  return (CONSENT_MONTH_OPTIONS as readonly number[]).includes(n) ? n : DEFAULT_CONSENT_MONTHS;
}

/** Onayın geçerlilik süresi (saniye): ay × 30 gün */
export const consentMaxAgeSeconds = (months: number) => Math.round(months * 30 * 24 * 60 * 60);

/** Çerez değeri: "<sürüm>.<0|1>.<zaman>" — ör. "1.1.1791452400" */
export function encodeConsent(record: ConsentRecord): string {
  return `${record.version}.${record.analytics ? 1 : 0}.${Math.floor(record.decidedAt)}`;
}

/** Çerez değerini okur; biçim bozuksa null (sorulmamış sayılır) */
export function decodeConsent(value: string | undefined | null): ConsentRecord | null {
  if (!value) return null;
  let raw = value;
  try {
    raw = decodeURIComponent(value);
  } catch {
    return null;
  }
  const m = /^([A-Za-z0-9._-]{1,20})\.([01])\.(\d{9,11})$/.exec(raw);
  if (!m) return null;
  return { version: m[1], analytics: m[2] === "1", decidedAt: Number(m[3]) };
}

/**
 * Kayıtlı tercihin bugünkü durumu: sürüm farklıysa (politika değişti), süresi dolmuşsa ya da karar zamanı gelecekteyse
 * (saat oynatılmış/bozuk) yeniden sorulur.
 */
export function consentStatus(record: ConsentRecord | null, opts: { version: string; months: number; now: number }): ConsentStatus {
  if (!record) return "unknown";
  if (record.version !== opts.version) return "unknown";
  const age = opts.now - record.decidedAt;
  if (age < -300 || age > consentMaxAgeSeconds(opts.months)) return "unknown";
  return record.analytics ? "granted" : "denied";
}

/** document.cookie'den bir çerezin değeri */
export function readCookie(cookieHeader: string, name: string): string | null {
  for (const part of cookieHeader.split(";")) {
    const i = part.indexOf("=");
    if (i < 0) continue;
    if (part.slice(0, i).trim() === name) return part.slice(i + 1).trim();
  }
  return null;
}

/** Onay çerezini yazan Set-Cookie değeri (document.cookie için): birinci taraf, Lax, HTTPS'te Secure */
export function consentCookieString(record: ConsentRecord, opts: { months: number; secure: boolean }): string {
  return [
    `${CONSENT_COOKIE}=${encodeURIComponent(encodeConsent(record))}`,
    "Path=/",
    `Max-Age=${consentMaxAgeSeconds(opts.months)}`,
    "SameSite=Lax",
    ...(opts.secure ? ["Secure"] : []),
  ].join("; ");
}

/** Google Analytics çerezleri: _ga, _ga_<kimlik>, (eski) _gid, _gat… Onay geri alınınca silinir. */
export const GA_COOKIE = /^(?:_ga(?:_[A-Za-z0-9]+)?|_gid|_gat(?:_[A-Za-z0-9_-]+)?|_gac_[A-Za-z0-9_-]+)$/;

/** document.cookie'deki Google Analytics çerezlerinin adları */
export function gaCookieNames(cookieHeader: string): string[] {
  return cookieHeader
    .split(";")
    .map((p) => p.split("=")[0]?.trim() ?? "")
    .filter((n) => GA_COOKIE.test(n));
}

/**
 * Bir çerezi silmek için yazılacak değerler: GA çerezi alan adının en üst kayıtlı düzeyine (".flyexpress.com") yazılır;
 * hangi düzeyde olduğu tarayıcıdan okunamadığı için ana bilgisayar adı ve her üst alan (nokta önekli) denenir.
 */
export function cookieDeletionStrings(name: string, hostname: string): string[] {
  const out = [`${name}=; Path=/; Max-Age=0`];
  const parts = hostname.split(".");
  // IP adresi ya da "localhost": yalnızca alan adı belirtilmeden
  if (parts.length < 2 || /^\d+$/.test(parts.at(-1) ?? "")) return out;
  for (let i = 0; i <= parts.length - 2; i++) {
    const domain = parts.slice(i).join(".");
    out.push(`${name}=; Path=/; Max-Age=0; Domain=${domain}`, `${name}=; Path=/; Max-Age=0; Domain=.${domain}`);
  }
  return out;
}
